import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Alert, Col, FormGroup, Button } from 'reactstrap';
import { InputField, RHFField } from '../../components/Field';
import { GatewayApi } from '../../gatewayApi';
import { sleep } from '../../utils';
import {
  arrToObjForFieldArr,
  ColLeft,
  ColRight,
  inputContentTypeList,
  RenderParameters,
  RenderTransforms,
} from './utils';

// Migrated from redux-form to react-hook-form. The request/response transforms,
// parameters and headers FieldArrays are now the Render* components (useFieldArray);
// the `state.form.*` init guard became an initialValues check. The `enabled` checkbox
// uses register on a native input (the Controller adapter mis-reads checkbox events).
function IntegrationsOperationsEditForm({
  currentStore,
  currentGatewayStore,
  storeIntegration,
  storeIntegrationOperation,
  initialValues,
}) {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);

  const { control, register, handleSubmit, reset } = useForm({ defaultValues: initialValues || {} });

  const initKey = JSON.stringify(initialValues);
  useEffect(() => {
    if (initialValues) reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initKey]);

  const submit = async (store) => {
    try {
      const settings = {};
      if (store.settings) {
        store.settings.forEach((item) => {
          settings[item.name] = item.value;
        });
      }

      let dataRequest = {},
        dataResponse = {};
      {
        let { parameters, headers, ...request } = store.request;
        dataRequest = {
          parameters: arrToObjForFieldArr(parameters),
          headers: arrToObjForFieldArr(headers),
          ...request,
        };
      }
      {
        let { parameters, headers, ...response } = store.response;
        dataResponse = {
          parameters: arrToObjForFieldArr(parameters),
          headers: arrToObjForFieldArr(headers),
          ...response,
        };
      }

      const data = {
        enabled: store.enabled,
        storeId: currentGatewayStore.storeId,
        integration: storeIntegration.integration,
        interface: storeIntegration.interface.interface,
        interfaceType: store.interfaceType,
        operation: store.operation,
        override: true,
        product: storeIntegration.interface.product,
        provider: storeIntegration.interface.provider,
        version: storeIntegration.interface.version,
        request: dataRequest,
        response: dataResponse,
        settings,
      };

      await GatewayApi.saveStoreIntegrationOperation(
        currentGatewayStore.storeId,
        storeIntegration.integration,
        storeIntegrationOperation.operation,
        data
      );
      setMessage('Saved successfully');
      setError(false);
      await sleep(1000);
      navigate(`/stores/${currentStore.id}/gateway/integrations/${storeIntegration.integration}`);
    } catch (err) {
      console.error('error:', err);
      setMessage('Failed to save, reason: ' + err.message);
      setError(true);
    }

    return false;
  };

  const onCancel = () => {
    navigate(-1);
  };

  const onDelete = async () => {
    if (!window.confirm('Are you sure to delete this item?')) {
      return;
    }

    try {
      await GatewayApi.deleteStoreIntegrationOperation(
        currentGatewayStore.storeId,
        storeIntegration.integration,
        storeIntegrationOperation.operation
      );
      setMessage('Deleted successfully');
      setError(false);
      await sleep(1000);
      navigate(`/stores/${currentStore.id}/gateway/integrations/${storeIntegration.integration}`);
    } catch (err) {
      console.error('error:', err);
      setMessage('Failed to delete, reason: ' + err.message);
      setError(true);
    }
  };

  if (!storeIntegration || !initialValues) {
    return <>Loading...</>;
  }

  return (
    <>
      <form onSubmit={handleSubmit(submit)} className="form-horizontal">
        {message && <Alert color={error ? 'danger' : 'success'}>{message}</Alert>}
        <FormGroup row>
          <ColLeft>Operation</ColLeft>
          <ColRight>
            <RHFField name="operation" control={control} component={InputField} id="operation" required />
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Enabled</ColLeft>
          <ColRight>
            <input type="checkbox" id="enabled" className="form-check-input" {...register('enabled')} />
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Interface Type</ColLeft>
          <ColRight>
            <select {...register('interfaceType')} required className="form-control" id="interfaceType">
              <option value="HTTP">HTTP</option>
              <option value="SOAP">SOAP</option>
            </select>
          </ColRight>
        </FormGroup>
        <FormGroup row style={{ backgroundColor: '#e8e8e8', paddingTop: 5, paddingBottom: 5 }}>
          <Col sm={12}>
            <strong>Request</strong>
          </Col>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Request content type</ColLeft>
          <ColRight>
            <select
              {...register('request.inputContentType')}
              required
              className="form-control"
              id="request.inputContentType"
            >
              {inputContentTypeList.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </ColRight>
        </FormGroup>
        <RenderTransforms control={control} register={register} name="request.transforms" />
        <FormGroup row>
          <ColLeft>Send content type</ColLeft>
          <ColRight>
            <select
              {...register('request.outputContentType')}
              required
              className="form-control"
              id="request.outputContentType"
            >
              {inputContentTypeList.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Send Method</ColLeft>
          <ColRight>
            <select {...register('request.method')} required className="form-control" id="request.method">
              {['GET', 'POST', 'PUT', 'DELETE'].map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </ColRight>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Send Path</ColLeft>
          <ColRight>
            <RHFField name="request.path" control={control} component={InputField} id="request.path" />
          </ColRight>
        </FormGroup>
        <RenderParameters control={control} register={register} name="request.parameters" title="Parameters" />

        <RenderParameters control={control} register={register} name="request.headers" title="Headers" />

        <FormGroup row style={{ backgroundColor: '#e8e8e8', paddingTop: 5, paddingBottom: 5 }}>
          <Col sm={12}>
            <strong>Response</strong>
          </Col>
        </FormGroup>
        <FormGroup row>
          <ColLeft>Receive content type</ColLeft>
          <ColRight>
            <select
              {...register('response.inputContentType')}
              required
              className="form-control"
              id="response.inputContentType"
            >
              {inputContentTypeList.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </ColRight>
        </FormGroup>
        <RenderTransforms control={control} register={register} name="response.transforms" />
        <FormGroup row>
          <ColLeft>Response content type</ColLeft>
          <ColRight>
            <select
              {...register('response.outputContentType')}
              required
              className="form-control"
              id="response.outputContentType"
            >
              {inputContentTypeList.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </ColRight>
        </FormGroup>
        <div>
          <hr size={1} />
        </div>
        {storeIntegrationOperation && (
          <div className="float-start">
            <Button type="button" color="danger" className="me-2" onClick={() => onDelete()}>
              Delete
            </Button>
          </div>
        )}
        <div className="float-end">
          <Button type="button" color="secondary" className="me-2" onClick={() => onCancel()}>
            Cancel
          </Button>
          <Button type="submit" color="primary">
            Save
          </Button>
        </div>
        <div style={{ clear: 'both' }}></div>
        <div style={{ marginTop: 5 }}>{message && <Alert color={error ? 'danger' : 'success'}>{message}</Alert>}</div>
      </form>
    </>
  );
}

export default IntegrationsOperationsEditForm;
